// React
import { memo, useCallback } from "react";

// Servisni sloj
import {
  formatPricingValue,
  isMoneyKey,
  pricingValueUnit,
} from "services/api/pricing/keyOptions";
import { PricingAdjustmentType } from "services/api/pricing/schemeTypes";

// UI
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { NumberField } from "ui/components/TextField/NumberField";

// Lokalne komponente
import PricingStepIntro from "ui/modules/pricing/components/PricingStepIntro/PricingStepIntro";

// Tipovi i konstante
import { ADJUSTMENT_OPTIONS } from "../../consts";
import { OverrideAmountStepProps } from "../../types";

/** Koliko se mijenja i na koji način. Učinak se vidi na plohi lijevo. */
const OverrideAmountStep: React.FC<OverrideAmountStepProps> = (
  props: OverrideAmountStepProps
) => {
  const { formValues, setFormValues, errors, keyOption } = props;

  const isMoney = isMoneyKey(keyOption);
  const isAbsolute = formValues.adjustmentType === "Absolute";

  const handleTypeChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      setFormValues((previous) => ({
        ...previous,
        adjustmentType: item.value as PricingAdjustmentType,
      }));
    },
    [setFormValues]
  );

  const handleValueChange = useCallback(
    (value: number | null) => {
      setFormValues((previous) => ({ ...previous, value }));
    },
    [setFormValues]
  );

  /*
    Dani, komadi i postotak ne trpe „popust": upisuje se nova vrijednost, a ne odstupanje od
    stare. Postotak od probnog perioda nema značenje, a kod ograničenja je i smjer obrnut —
    veći broj je pogodnost, ne kazna.
  */
  if (!isMoney) {
    const unit = keyOption ? pricingValueUnit(keyOption.valueKind) : "";

    return (
      <>
        <PricingStepIntro
          title="Nova vrijednost"
          description={
            <>
              Ovdje se ne daje popust nego se <strong>upisuje nova vrijednost</strong> koja za
              ovog klijenta zamjenjuje onu iz cjenika. Vrijedi dok traje rok, a onda se klijent
              vraća na cjenik.
            </>
          }
        />

        <div className="flex flex-col gap-4 p-4 w-full max-w-4xl mx-auto">
          <div className="border border-slate-200 bg-white px-4 py-3">
            <p className="text-xs text-slate-500">Iz cjenika</p>

            <p className="text-lg font-semibold text-slate-700">
              {keyOption
                ? formatPricingValue(keyOption.rawValue, keyOption.valueKind)
                : "—"}
            </p>
          </div>

          <div className="w-[220px]">
            <NumberField
              name="value"
              label="Nova vrijednost"
              value={formValues.value}
              onChange={handleValueChange}
              error={errors.value}
              groupAppend={unit}
              decimals={keyOption?.valueKind === "Percent" ? 2 : 0}
              size="full"
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PricingStepIntro
        title="Promjena"
        description={
          <>
            Postotak <strong>prati cjenik</strong>, pa klijent ide gore zajedno s njim. Fiksni
            iznos zamrzava cijenu i s vremenom tiho poveća popust. Učinak se odmah vidi lijevo.
          </>
        }
      />

      <div className="flex flex-col gap-4 p-4 w-full max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Dropdown
              name="adjustmentType"
              label="Tip"
              value={formValues.adjustmentType}
              options={ADJUSTMENT_OPTIONS}
              onChange={handleTypeChange}
              width="full"
            />
          </div>

          <div className="w-[200px]">
            <NumberField
              name="value"
              label="Promjena"
              value={formValues.value}
              onChange={handleValueChange}
              error={errors.value}
              groupAppend={isAbsolute ? "€" : "%"}
              decimals={2}
              allowNegative={!isAbsolute}
              size="full"
            />
          </div>
        </div>

        {!isAbsolute && (
          <p className="text-sm text-slate-500">
            Popust se upisuje kao negativan broj, npr. −25 za četvrtinu niže.
          </p>
        )}
      </div>
    </>
  );
};

export default memo(OverrideAmountStep);
